import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, MapPin, Phone, Mail, Calendar, Building, Heart, Brain, Activity, Ambulance, Microscope, Pill, Cross, Shield, Zap, Eye, Armchair as Wheelchair, Leaf, Smile, Dumbbell, Target, Stethoscope, Users } from '../lib/icons';
import { useShowcaseDoctors } from '../hooks/useDoctors';
import { ProviderType } from '../lib/supabase';
import { getProviderTypeLabel } from '../utils/providerTypeUtils';
import { getProfileImage } from '../utils/imageUtils';
import { createDoctorSlug } from '../utils/slugUtils';
import RatingDisplay from './RatingDisplay';
import DoctorSpecialtiesDisplay from './DoctorSpecialtiesDisplay';

const ProviderShowcase: React.FC = () => {
  // Lädt nur die 6 neuesten aktiven Provider direkt von der DB. Vorher
  // useDoctors() — das ladete alle 11k Records, blockierte den Hauptthread
  // bei React-Hydration und produzierte 7+ MB JSON-Payload pro Home-Visit.
  const { doctors, loading, error } = useShowcaseDoctors(6);
  const navigate = useNavigate();

  const getTypeIcon = (type: ProviderType) => {
    switch (type) {
      case 'doctor': return Stethoscope;
      case 'group_practice': return Users;
      case 'clinic': return Building;
      case 'naturopath': return Leaf;
      case 'psychotherapist': return Brain;
      case 'therapy_practice': return Activity;
      case 'ambulanz': return Ambulance;
      case 'fachklinik': return Cross;
      case 'labor_diagnostik': return Microscope;
      case 'mvz': return Shield;
      case 'naturheilpraxis': return Heart;
      case 'pflegedienst': return Wheelchair;
      case 'reha': return Zap;
      case 'dentist': return Smile;
      case 'sonstiges': return Eye;
      default: return Stethoscope;
    }
  };

  const getTypeLabel = (type: ProviderType) => {
    switch (type) {
      case 'doctor': return 'Arzt / Ärztin';
      case 'group_practice': return 'Gemeinschaftspraxis';
      case 'clinic': return 'Klinik / Krankenhaus';
      case 'naturopath': return 'Heilpraktiker / Heilpraktikerin';
      case 'psychotherapist': return 'Psychotherapeut / Psychotherapeutin';
      case 'therapy_practice': return 'Therapeutische Praxis';
      default: return 'Anbieter';
    }
  };

  const handleProviderClick = (providerId: string) => {
    const d = doctors.find((x) => x.id === providerId);
    const slug = d ? createDoctorSlug(d) : providerId;
    navigate(`/anbieter/${slug}`);
  };

  // useShowcaseDoctors(6) liefert bereits die 6 neuesten aktiven —
  // hier nur noch durchreichen.
  const latestProviders = doctors;


  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Lade Gesundheitsanbieter...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600">Fehler beim Laden der Anbieter: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Gesundheitsanbieter finden
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Qualifizierte Gesundheitsexperten in Ihrer Nähe – von Ärzten bis zu therapeutischen Praxen
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {latestProviders.map((provider) => {
            const TypeIcon = getTypeIcon(provider.type);
            
            return (
              <ProviderCard
                key={provider.id}
                provider={provider}
                TypeIcon={TypeIcon}
                onProviderClick={handleProviderClick}
              />
            );
          })}
        </div>

        {latestProviders.length === 0 && !loading && (
          <div className="text-center py-12">
            <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Noch keine Anbieter verfügbar
            </h3>
            <p className="text-gray-600 mb-8">
              Derzeit sind noch keine Gesundheitsanbieter in der Datenbank registriert.
            </p>
          </div>
        )}

        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/anbieter')}
            className="bg-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center mx-auto"
          >
            Alle Anbieter anzeigen
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    </section>
  );
};

// Separate component to avoid hooks being called inside map
interface ProviderCardProps {
  provider: any;
  TypeIcon: any;
  onProviderClick: (id: string) => void;
}

const ProviderCard: React.FC<ProviderCardProps> = ({ provider, TypeIcon, onProviderClick }) => {
  // useProviderImages bewusst nicht aufgerufen: ProviderShowcase rendert N
  // Cards (~6-8) und ein Hook pro Card hatte N×provider_images-Queries
  // ausgelöst (Lighthouse meldete sie auch als ERR_ACCESS_DENIED-Console-
  // Errors). Die Showcase nutzt nur das Hauptbild → provider.image_url
  // reicht; getProfileImage fällt auf einen typ-spezifischen Default zurück,
  // wenn das Feld leer ist. Detail-Galerie auf DoctorDetail bleibt unberührt.

  const getTypeLabel = (type: ProviderType) => {
    switch (type) {
      case 'doctor': return 'Arzt / Ärztin';
      case 'group_practice': return 'Gemeinschaftspraxis';
      case 'clinic': return 'Klinik / Krankenhaus';
      case 'naturopath': return 'Heilpraktiker / Heilpraktikerin';
      case 'psychotherapist': return 'Psychotherapeut / Psychotherapeutin';
      case 'therapy_practice': return 'Therapeutische Praxis';
      default: return 'Anbieter';
    }
  };

  return (
    <div
      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 overflow-hidden group"
      onClick={() => onProviderClick(provider.id)}
    >
      {/* Provider Image */}
      <div className="h-40 sm:h-48 w-full">
        <img
          src={getProfileImage(provider.image_url, undefined, provider.type)}
          alt={provider.provider_name}
          className="w-full h-full object-cover rounded-t-lg group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          decoding="async"
          fetchpriority="low"
          width={400}
          height={400}
        />
      </div>
      
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <TypeIcon className="w-6 h-6 text-blue-600 mr-2" />
            <span className="text-sm text-gray-600">{getTypeLabel(provider.type)}</span>
          </div>
          {provider.is_premium && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              Premium
            </span>
          )}
        </div>
        
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
          {provider.provider_name}
        </h3>
        
       {/* Fachgebiete prominent unter dem Namen */}
       <div className="mb-4">
         <DoctorSpecialtiesDisplay doctorId={provider.id} />
       </div>
       
        <div className="flex items-center text-gray-600 mb-3">
          <MapPin className="w-4 h-4 mr-1" />
          <span className="text-sm">
            {provider.city}, {provider.bundesland}
          </span>
        </div>
        
       <div className="mb-4">
          <RatingDisplay doctorId={provider.id} className="text-sm" />
        </div>


        <div className="grid grid-cols-2 gap-2">
          {provider.profile_phone && (
            <a
              href={`tel:${provider.profile_phone}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
            >
              <Phone className="w-4 h-4 mr-2" />
              Anrufen
            </a>
          )}
          {provider.profile_email && provider.show_email && (
            <a
              href={`mailto:${provider.profile_email}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="flex items-center justify-center px-3 py-2 hover:bg-green-800 transition-colors text-xs font-medium"
            >
              <Mail className="w-4 h-4 mr-2" />
              E-Mail
            </a>
          )}
          
          {/* Online Booking Button - spans full width when available */}
          {provider.online_booking_enabled && provider.online_booking_url && (
            <a
              href={provider.online_booking_url.startsWith('http://') || provider.online_booking_url.startsWith('https://') ? provider.online_booking_url : `https://${provider.online_booking_url}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="col-span-2 flex items-center justify-center px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs font-medium"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Online Termin buchen
            </a>
          )}
          
          {/* Details Button - adjusts based on available actions */}
          <button 
            className={`flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-medium ${
              (!provider.profile_phone && !provider.show_email && (!provider.online_booking_enabled || !provider.online_booking_url)) ? 'col-span-2' : 
              (provider.online_booking_enabled && provider.online_booking_url) ? '' : 'col-span-2'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onProviderClick(provider.id);
            }}
          >
            Details anzeigen
            <ChevronRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProviderShowcase;